<template>
  <div>
    <b-card>
      <section name="header">
        <div class="organism-site_header card-padding">
          <span class="header__label">Site Location</span>
        </div>
      </section>

      <section name="site-detail">
        <div class="px-2 pb-2">
          <div class="row">
            <div class="col-12 col-md-5">
              <atom-input
                v-model="siteDetail.name"
                title="Site Name"
                placeholder="Site name"
                :isDisable="true"
                error-message="Please enter site name"
              />
            </div>
            <div v-if="siteId" class="col-12 col-md-7 mt-1 mt-md-0">
              <div class="organism-site_avatar text-uppercase">
                Last Modified
              </div>
              <atom-author-avatar
                avatar-variant="light-secondary"
                :author-name="siteDetail.updatedBy"
                :datetime="siteDetail.updatedAt"
              />
            </div>
          </div>
        </div>
      </section>

      <hr class="m-0" />

      <section name="locations">
        <div
          class="p-2 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between"
        >
          <div>
            <span class="sub-header__label">Locations</span>
            <span class="description pl-1"
              >{{ locationList.length }} item(s)</span
            >
          </div>
          <atom-button
            button-id="create-new-location"
            class="add__button mt-1 mt-sm-0"
            icon-left="PlusIcon"
            button-style="border"
            @click="openCreateLocation"
            >Add New Location
          </atom-button>
        </div>

        <div v-if="locationList.length" class="px-2 pb-2 department-list">
          <molecule-location-item
            v-for="(location, index) in locationList"
            :key="index"
            :index="index + 1"
            :name="location.name"
            @onClickEdit="getLocationDetail(location)"
            @onClickDelete="openModalDeleteLocation(location)"
          />
        </div>

        <div v-if="!locationList.length" class="px-2 pb-2">No Locations</div>
      </section>

      <hr class="m-0" />

      <section name="footer">
        <div class="card-padding">
          <div class="d-flex justify-content-end mt-2">
            <atom-button
              button-id="create-new-button"
              class="mr-1"
              @click="handleUpdateLocation"
              >Save Changes
            </atom-button>
            <atom-button
              button-id="cancel-button"
              button-style="border"
              button-color="secondary"
              @click="onClickCancelSite"
              >Cancel
            </atom-button>
          </div>
        </div>
      </section>

      <section name="modal-location">
        <molecule-modal-dialog
          :modal-id="modalLocationId"
          :text-title="textHeaderLocation"
          is-slot-body
          :is-close-on-back-drop="false"
          :button-text-left="textButton"
          :is-disable-button-left="isShowValidateLocation && isErrorLocation"
          @clickButtonLeft="handleLocation"
        >
          <template #body>
            <div class="w-100 px-md-4 mt-1">
              <atom-input
                v-model="locationDetail.name"
                title="Location Name"
                placeholder="Location Name"
                :is-error="isShowValidateLocation && isErrorLocation"
                :is-asterisk="true"
                error-message="Please enter location name"
                class="mt-1"
              />
            </div>
          </template>
        </molecule-modal-dialog>

        <molecule-modal-dialog
          :modal-id="modalDeleteId"
          :is-text-center="true"
          :text-title="`Do you want to delete \n location &quot;${locationDelete.name}?&quot;`"
          button-text-left="Delete"
          button-color-left="danger"
          @clickButtonLeft="clickConfirmDelete"
          @clickButtonRight="closeModalDelete"
          @clickIconClose="closeModalDelete"
        />
      </section>
    </b-card>
    <footer style="height: 1px"></footer>
  </div>
</template>

<script>
import { BCard, BTooltip } from "bootstrap-vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomAuthorAvatar from "@/components/atoms/AtomAuthorAvatar.vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import {
  updateLocationBySiteId,
  getLocationBySiteId,
} from "@/api/api-module-data"
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
import { DateTime } from "luxon"
import MoleculeLocationItem from "@/components/molecules/globalSetting/dataManagement/site/MoleculeLocationItem.vue"

export default {
  name: "OrganismSiteCreateEdit",
  components: {
    BCard,
    BTooltip,
    AtomInput,
    AtomAuthorAvatar,
    AtomButton,
    AtomSelect,
    MoleculeModalDialog,
    MoleculeLocationItem,
  },
  mixins: [mixins, mixinsValidator],
  data() {
    return {
      siteDetail: {
        id: "",
        name: "",
        updatedAt: "",
        updatedBy: "",
      },

      modalLocationId: "modal-create-location",
      modalDeleteId: "modal-delete",

      isShowValidateSite: false,

      locationList: [],
      isShowValidateLocation: false,

      locationDefault: {
        id: "",
        name: "",
      },

      locationDetail: {
        id: "",
        name: "",
      },
      locationDelete: {},
      locationIdList: [],
    }
  },
  computed: {
    siteId() {
      return this.$route.params.siteId
    },

    isErrorLocation() {
      return this.locationDetail?.name?.trim() === ""
    },

    textButton() {
      return this.locationDetail.id ? "Save Changes" : "Add"
    },

    textHeaderLocation() {
      return this.locationDetail.id ? "Location Detail" : "New Location"
    },
  },
  mounted() {
    if (this.siteId) this.getSiteDetail(this.siteId)
  },
  methods: {
    async getSiteDetail(siteId) {
      this.showLoading(true)
      await getLocationBySiteId(siteId)
        .then((response) => {
          this.siteDetail = {
            id: response.siteId,
            name: response.siteName,
            updatedAt: response.updatedAt,
            updatedBy: response.updatedBy,
          }
          this.locationList = response.locations.map((location) => ({
            id: location.locationId,
            name: location.locationName,
            sequence: location.sequence,
            locationFullNameEn: location.locationFullNameEn,
            locationFullNameTh: location.locationFullNameTh,
          }))

          this.locationIdList = this.locationList.map((item) => item.id)
        })
        .finally(() => this.showLoading(false))
    },

    openCreateLocation() {
      this.isShowValidateLocation = false
      this.locationDetail = { ...this.locationDefault }
      this.$bvModal.show(this.modalLocationId)
    },

    handleLocation() {
      this.isShowValidateLocation = true

      this.$nextTick(() => {
        if (!this.isErrorLocation) {
          this.closeModalLocation()

          if (this.locationDetail.id) this.updateLocation()
          else this.addLocation()
        }
      })
    },

    addLocation() {
      this.locationList.push({
        id: `new-location-${DateTime.now()}`,
        name: this.locationDetail?.name?.trim(),
      })
    },

    updateLocation() {
      const index = this.locationList.findIndex(
        (item) => item.id === this.locationDetail.id
      )

      this.locationList[index].name = this.locationDetail.name
    },

    getLocationDetail(location) {
      this.locationDetail = { ...location }
      this.$bvModal.show(this.modalLocationId)
    },

    onClickCancelSite() {
      this.$router.push("/global-settings/data-management/location")
    },

    openModalDeleteLocation(location) {
      this.locationDelete = location
      this.$bvModal.show(this.modalDeleteId)
    },

    closeModalLocation() {
      this.isShowValidateLocation = false
      this.$bvModal.hide(this.modalLocationId)
    },

    clickConfirmDelete() {
      if (this.locationDelete.id) {
        this.locationList = this.locationList.filter((location) => {
          return location.id !== this.locationDelete.id
        })
      }

      this.closeModalDelete()
    },

    closeModalDelete() {
      this.locationDelete = {}
      this.$bvModal.hide(this.modalDeleteId)
    },

    handleUpdateLocation() {
      if (this.siteId) this.updateLocationListToApi()
    },

    async updateLocationListToApi() {
      await updateLocationBySiteId(this.siteId, {
        locations: this.formatLocation(),
      })
        .then(() => {
          this.toastSuccess("Location Updated")
        })
        .catch((error) => {
          error.response.data.ErrorCode === "IN_USE"
            ? this.toastError(error.response.data.ErrorMessage)
            : this.toastError("Location Update Failed")
        })
        .finally(() => {
          this.getSiteDetail(this.siteId)
        })
    },

    formatLocation() {
      return this.locationList.map((item) => {
        const location = {
          locationName: item.name,
          sequence: 0,
          locationFullNameTh: item.locationFullNameTh || item.name,
          locationFullNameEn: item.locationFullNameEn || item.name,
        }

        if (this.locationIdList.includes(item.id)) {
          return { ...location, locationId: item.id }
        }

        return location
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.organism-site_header {
  display: flex;
  flex-direction: column;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.sub-header__label {
  font-weight: 600;
  font-size: 14px;
  color: #5e5873;
}

.description {
  font-size: 12px;
  color: #b9b9c3;
}

.card-body {
  padding: 0;
}

.card-padding {
  padding: 1.5rem;
}

.organism-site_avatar {
  font-size: 12px;
  margin-bottom: 5px;
}

.department-list > * + * {
  margin-top: 8px;
}

@media screen and (min-width: 576px) {
  .organism-site_header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
